import { ChangeEvent, useMemo } from "react";
import { useUser, useModal, useAlerts } from "backend";
import { getFunctions, httpsCallable } from "firebase/functions";

import {
  Box,
  CreatorQuestions as questions,
  TextVariants,
  Colors,
  Text,
  Button,
  CreatorQuestionIds,
  LOCAL_STORAGE_KEYS,
  capitalize,
} from "reusable";
import { Question, Step } from "../sign-up/Question";

import RenderInputField from "../sign-up/components/RenderInputField";
import "./CreatorQuestions.css";
import { CompleteProfile, CreatorProfile } from "models";

const CreatorQuestions = (props: {
  step: number;
  setStep: (difference: number) => void;
  setCreatorAnswers: (creatorProfile: CompleteProfile) => void;
  creatorAnswers: CompleteProfile;
}): JSX.Element => {
  const { step, setStep, creatorAnswers, setCreatorAnswers } = props;
  const creatorQuestionJson = questions as unknown as Array<Step>;
  const { isLoading, loggedInUser, isPremiumUser, uploadPhotoLimit } = useUser();
  const { setIsPremiumModalOpen } = useModal();
  const { addAlert } = useAlerts();

  //states - using computed validation instead of useState to prevent infinite loops

  //functions
  const handleAnswer = async (
    event: ChangeEvent<HTMLInputElement>,
    answeredQuestion: Question
  ) => {
    const { questionID, type } = answeredQuestion;
    const answer = event.target.value;
    const target = event.target;

    if (type === "checkbox") {
      const currentAnswers =
        (creatorAnswers as any)[questionID]?.split(", ") ?? [];
      const multiple = Boolean((event as any)?.multiple);
      const checked = (target as HTMLInputElement).checked
        ? (multiple ? [...currentAnswers, answer] : [answer])
            ?.filter((a) => a !== "")
            .join(", ")
        : currentAnswers.filter((a: string) => a !== answer).join(", ");

      setCreatorAnswers({ ...creatorAnswers, [questionID]: checked });
    } else if (type === "photo") {
      const existingFiles = (target as HTMLInputElement)?.files || [];
      const newFiles = [
        ...(creatorAnswers?.newCreatorImages || []),
        ...((target as any)?.newCreatorImages || []),
      ];
      const newProfilePicture =
        (target as any)?.newProfilePicture ||
        creatorAnswers?.newProfilePicture ||
        [];
      const removedFiles = [
        ...(creatorAnswers?.removedUrls || []),
        (target as any)?.removedUrl || "",
      ];
      const updatedFiles = Array.from(existingFiles || [])?.filter(
        (url: any) => url !== ((target as any)?.removedUrl as string)
      );

      // Enforce photo upload limits
      const maxPhotos = uploadPhotoLimit;
      if (updatedFiles.length > maxPhotos) {
        addAlert({
          type: "error",
          message: `You can upload up to ${maxPhotos} photos${isPremiumUser ? " as a premium user." : ". Upgrade to premium for more."}`
        });
        return;
      }

      setCreatorAnswers({
        ...creatorAnswers,
        [questionID]: [...updatedFiles],
        removedUrls: [...removedFiles],
        newCreatorImages: newFiles,
        newProfilePicture: [...newProfilePicture],
      });
    } else if (type === "switch") {
      setCreatorAnswers({
        ...creatorAnswers,
        [questionID]: answer,
      });

      // Simple job functionality - fire and forget
      if (questionID === 'lookingForJob' && answer === 'true' && loggedInUser?.userId) {
        const functions = getFunctions();
        const saveJobPreference = httpsCallable(functions, 'saveJobPreference');
        saveJobPreference({ userId: loggedInUser.userId, resume: creatorAnswers.resume || '' })
          .then(() => addAlert({ type: "success", message: "Job seeking activated" }))
          .catch(() => addAlert({ type: "error", message: "Failed to activate job seeking" }));
      } else if (questionID === 'isHiring' && answer === 'true' && loggedInUser?.userId) {
        const functions = getFunctions();
        const saveJobPosting = httpsCallable(functions, 'saveJobPosting');
        saveJobPosting({
          userId: loggedInUser.userId,
          jobPosition: creatorAnswers.jobPosition || '',
          requiredExperience: creatorAnswers.requiredExperience || '',
          requiredEducation: creatorAnswers.requiredEducation || '',
          jobSalary: creatorAnswers.jobSalary || '',
          requiredSoftwareSkills: creatorAnswers.requiredSoftwareSkills || '',
          jobType: creatorAnswers.jobType || 'Full-time',
          jobLocation: creatorAnswers.jobLocation || { lat: '', lng: '', place: '' },
          jobDescription: creatorAnswers.jobDescription || ''
        })
          .then(() => addAlert({ type: "success", message: "Hiring status activated" }))
          .catch(() => addAlert({ type: "error", message: "Failed to activate hiring" }));
      }
    } else if (type === "theme") {
      const selectedTheme = (event as any)?.theme || {};
      if (selectedTheme?.background?.length) {
        setCreatorAnswers({
          ...creatorAnswers,
          [questionID]: selectedTheme,
        });
      }
    } else {
      setCreatorAnswers({
        ...creatorAnswers,
        [questionID]:
          questionID === "contactNumber" ? answer?.slice(0, 10) : answer,
        name: capitalize(
          `${creatorAnswers?.firstName} ${creatorAnswers?.lastName}`
        ),
      });
    }
    
    // Save to localStorage for new users
    if (
      loggedInUser &&
      !loggedInUser?.isCreatorQuestionsAnswered &&
      questionID !== CreatorQuestionIds.CREATOR_PHOTOS &&
      questionID !== CreatorQuestionIds.PROFILE_PICTURE
    ) {
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.CREATOR_PROFILE,
        JSON.stringify({
          ...creatorAnswers,
          [questionID]: answer,
        })
      );
    }
  };

  const handleNextStep = () => {
    window.scrollTo(0, 0);
    setStep(1);
  };

  // Simple currentStep access - no need to memoize simple array access
  const currentStep = creatorQuestionJson[step - 1];

  // Simple validation with useMemo to prevent infinite loops  
  const isAllRequiredAnswered = useMemo(() => {
    if (!currentStep?.questions || !creatorAnswers) {
      console.log('[Validation] No currentStep or creatorAnswers');
      return false;
    }
    
    const visibleQuestions = currentStep.questions.filter((question) => {
      const { questionID, roles } = question;
      
      // Use the same filtering logic as the display
      const businessOwnerQuestions = [
        "occupation", "position", "firstName", "lastName", "contactNumber",
        "location", "profilePicture", "about", "languages", "experienceYears",
        "completedProjects", "projectCost", "projectType", "creatorPhotos", "companyName", "isHiring"
      ];
      const businessPremiumQuestions = ["websiteUrl", "instagramLink", "theme"];
      const jobPostingQuestions = [
        "jobPosition", "requiredExperience", "requiredEducation", "jobSalary",
        "requiredSoftwareSkills", "jobType", "jobDescription"
      ];
      const currentBusinessQuestions = [
        ...businessOwnerQuestions,
        ...(creatorAnswers?.isSubscribed ? businessPremiumQuestions : []),
        ...(creatorAnswers?.isHiring ? jobPostingQuestions : []),
      ];

      const nonBusinessQuestions = [
        "occupation", "position", "firstName", "lastName", "contactNumber",
        "location", "profilePicture", "about", "languages", "experienceYears",
        "companyName", "graduationYear", "softwareSkills", "creatorPhotos", 
        "universityName", "lookingForJob"
      ];
      const nonBusinessPremiumQuestions = ["websiteUrl", "instagramLink", "theme"];
      const jobSeekerQuestions = ["resume"];
      const currentNonBusinessQuestions = [
        ...nonBusinessQuestions,
        ...(creatorAnswers?.isSubscribed ? nonBusinessPremiumQuestions : []),
        ...(creatorAnswers?.lookingForJob ? jobSeekerQuestions : []),
      ];

      // Check if question should be visible based on user type
      const isQuestionVisible = (creatorAnswers?.position?.includes("Business Owner")
        ? currentBusinessQuestions.includes(questionID)
        : currentNonBusinessQuestions.includes(questionID));
      
      // Also check role-based filtering if roles are specified
      const isRoleAllowed = roles?.length 
        ? roles.includes(creatorAnswers?.position)
        : true;
      
      return isQuestionVisible && isRoleAllowed;
    });
    
    const requiredQuestions = visibleQuestions.filter(q => q.isRequired);
    console.log('[Validation] Step:', step, 'Position:', creatorAnswers?.position);
    console.log('[Validation] Visible questions:', visibleQuestions.map(q => q.questionID));
    console.log('[Validation] Required questions:', requiredQuestions.map(q => q.questionID));
    
    requiredQuestions.forEach(q => {
      const answer = (creatorAnswers as any)[q.questionID];
      let isValid = false;
      
      // Special validation for different field types
      if (q.type === 'location-autocomplete') {
        isValid = answer && answer.place && answer.place.length > 0;
      } else if (q.type === 'photo') {
        isValid = answer && Array.isArray(answer) && answer.length > 0;
      } else if (q.type === 'switch') {
        isValid = true; // Switch fields are always valid (boolean)
      } else if (q.type === 'theme') {
        isValid = answer && answer.background && answer.background.length > 0;
      } else {
        isValid = answer && String(answer).trim().length > 0;
      }
      
      console.log(`[Validation] ${q.questionID} (${q.type}): "${JSON.stringify(answer)}" -> ${isValid}`);
    });
    
    const allValid = requiredQuestions.every(q => {
      const answer = (creatorAnswers as any)[q.questionID];
      
      // Special validation for different field types
      if (q.type === 'location-autocomplete') {
        return answer && answer.place && answer.place.length > 0;
      } else if (q.type === 'photo') {
        return answer && Array.isArray(answer) && answer.length > 0;
      } else if (q.type === 'switch') {
        return true; // Switch fields are always valid (boolean)
      } else if (q.type === 'theme') {
        return answer && answer.background && answer.background.length > 0;
      } else {
        return answer && String(answer).trim().length > 0;
      }
    });
    
    console.log('[Validation] All valid:', allValid);
    return allValid;
  }, [creatorAnswers, currentStep, step]);

  const titleArray = currentStep?.stepTitle?.split(" ") || [];
  const isFinalStep = creatorQuestionJson?.length === step;

  if (!currentStep) {
    return <Box className="creator-questions" isLoading={true}><Text>Loading...</Text></Box>;
  }

  return (
    <Box className="creator-questions" isLoading={isLoading}>
      <Box>
        <Box className="step-info responsive-60-40" textAlign="center">
          <Text
            color={Colors.BLACK_DARK}
            variant={TextVariants.HEADING_QUARTERNARY_FONT}
          >
            {titleArray[0]}
          </Text>
          <Text
            style={{ marginLeft: "7px" }}
            color={titleArray?.length > 2 ? Colors.BLACK_DARK : Colors.ORANGE}
            variant={TextVariants.HEADING_QUARTERNARY_FONT}
          >
            {titleArray[1]}
          </Text>

          {titleArray?.length > 2 && (
            <>
              <br />
              <Text
                style={{ marginLeft: "7px" }}
                color={Colors.ORANGE}
                variant={TextVariants.HEADING_QUARTERNARY_FONT}
              >
                {titleArray
                  ?.filter((_, i) => i > 1)
                  ?.map((v) =>
                    v?.includes("8")
                      ? !isPremiumUser
                        ? v
                        : v.replace("8", "30")
                      : v
                  )
                  ?.join(" ")}
              </Text>
            </>
          )}
          <Text
            variant={TextVariants.MEDIUM_PRIMARY_FONT}
            color={Colors.GRAY_5}
            className="step-description"
            style={{ lineHeight: "22.40px" }}
          >
            {currentStep?.description}
          </Text>
        </Box>
        {isFinalStep && !isPremiumUser && (
          <Box textAlign="center" width="100%" marginTop="-25px">
            <Text
              variant={TextVariants.MEDIUM_PRIMARY_FONT}
              color={Colors.GRAY_3}
              className="step-description"
              style={{ lineHeight: "22.40px" }}
            >
              Want to Upload 30 Photos + More Benefits
            </Text>
            <br />
            <Box
              margin="15px 0 25px 0"
              display="inline-block"
              onClick={() => setIsPremiumModalOpen(true)}
            >
              <Button className="button-large-round">
                <Box
                  className="inline-block"
                  marginBottom="3px"
                  marginRight="4px"
                >
                  💎
                </Box>
                Become a Pro Member
              </Button>
            </Box>
          </Box>
        )}

        {currentStep?.questions
          .filter((d) => {
            // Create question arrays inline to avoid dependency issues
            const businessOwnerQuestions = [
              "occupation", "position", "firstName", "lastName", "contactNumber",
              "location", "profilePicture", "about", "languages", "experienceYears",
              "completedProjects", "projectCost", "projectType", "creatorPhotos", "companyName", "isHiring"
            ];
            const businessPremiumQuestions = ["websiteUrl", "instagramLink", "theme"];
            const jobPostingQuestions = [
              "jobPosition", "requiredExperience", "requiredEducation", "jobSalary",
              "requiredSoftwareSkills", "jobType", "jobDescription"
            ];
            const currentBusinessQuestions = [
              ...businessOwnerQuestions,
              ...(creatorAnswers?.isSubscribed ? businessPremiumQuestions : []),
              ...(creatorAnswers?.isHiring ? jobPostingQuestions : []),
            ];

            const nonBusinessQuestions = [
              "occupation", "position", "firstName", "lastName", "contactNumber",
              "location", "profilePicture", "about", "languages", "experienceYears",
              "companyName", "graduationYear", "softwareSkills", "creatorPhotos", 
              "universityName", "lookingForJob"
            ];
            const nonBusinessPremiumQuestions = ["websiteUrl", "instagramLink", "theme"];
            const jobSeekerQuestions = ["resume"];
            const currentNonBusinessQuestions = [
              ...nonBusinessQuestions,
              ...(creatorAnswers?.isSubscribed ? nonBusinessPremiumQuestions : []),
              ...(creatorAnswers?.lookingForJob ? jobSeekerQuestions : []),
            ];

            return (creatorAnswers?.position?.includes("Business Owner")
              ? currentBusinessQuestions.includes(d.questionID)
              : currentNonBusinessQuestions.includes(d.questionID)) &&
            (d?.roles?.length
              ? d.roles.includes(creatorAnswers?.position)
              : true);
          })
          ?.map((question, i) => (
            <Box className="responsive-60-40" key={i}>
              <Box marginBottom="10px">
                <Text
                  variant={TextVariants.MEDIUM_TERNARY_FONT_EXTRA_BOLD}
                  style={{ display: "block", marginBottom: "10px" }}
                  className="creator-question"
                  color={Colors.BLACK_DARK}
                >
                  {question?.content}
                  {!isFinalStep && (
                    <Text
                      variant={TextVariants.MEDIUM_TERNARY_FONT_EXTRA_BOLD}
                      style={{ marginLeft: "3px", display: question.isRequired ? "inline-block" : "none" }}
                      color={Colors.ORANGE}
                    >
                      *
                    </Text>
                  )}
                </Text>
                {question?.subContent && (
                  <Text
                    variant={TextVariants.LARGE_TERNARY_FONT}
                    color={Colors.GRAY_1}
                    className="creator-sub-question"
                  >
                    {question?.subContent}
                  </Text>
                )}
              </Box>
              <RenderInputField
                value={(() => {
                  const value = (creatorAnswers as any)[question?.questionID];
                  if (value !== undefined && value !== null) return value;
                  
                  // Provide appropriate default values based on question type
                  switch (question.type) {
                    case "checkbox":
                    case "language-chip":
                    case "software-chip":
                      return "";
                    case "location-autocomplete":
                      return { lat: "", lng: "", place: "" };
                    case "photo":
                      return [];
                    case "switch":
                      return false;
                    case "theme":
                      return { background: "", color: "" };
                    default:
                      return "";
                  }
                })()}
                onChange={handleAnswer}
                question={question}
              />
            </Box>
          ))}
        <Box
          className="grid-x"
          scrollToViewPort={isFinalStep && isAllRequiredAnswered}
        >
          <Box marginRight="12px">
            <Button
              disabled={step === 1}
              onClick={() => {
                setStep(-1);
                window.scrollTo(0, 0);
              }}
              className="cell button-medium shrink"
            >
              Back
            </Button>
          </Box>
          <Button
            onClick={handleNextStep}
            className="cell next-step-button responsive-60-40 auto"
            disabled={!isAllRequiredAnswered}
          >
            {isFinalStep
              ? loggedInUser?.isCreatorQuestionsAnswered
                ? "Update Profile"
                : "Create Profile"
              : "Continue"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default CreatorQuestions;